import { Check } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import { Avatar } from '../tasks/linear/Avatar';

export interface MemberOption {
  id: string;
  name: string;
}

/**
 * Scrollable checkbox list for picking group members. A plain list rather than
 * ui/MultiSelect because that widget treats `[]` as "all", which is the wrong
 * semantic here (no selection = just the creator).
 */
export function MemberPicker({
  options,
  value,
  onChange,
  className = '',
}: {
  options: MemberOption[];
  value: string[];
  onChange: (ids: string[]) => void;
  className?: string;
}) {
  const { t } = useI18n();
  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  const allOn = options.length > 0 && options.every((o) => value.includes(o.id));

  return (
    <div
      className={`mt-1 max-h-56 space-y-0.5 overflow-y-auto rounded-md border border-border p-1 ${className}`}
    >
      {/* "Everyone" is the common case for a company-wide channel, and ticking
          twenty boxes one by one is how that channel ends up missing someone. */}
      {options.length > 1 && (
        <button
          type="button"
          onClick={() => onChange(allOn ? [] : options.map((o) => o.id))}
          className="mb-0.5 w-full rounded-md px-2 py-1 text-left text-xs font-semibold text-brand-600 transition-colors hover:bg-accent"
        >
          {allOn ? t('teamChat.selectNone') : t('teamChat.selectAll')}
        </button>
      )}
      {options.map((option) => {
        const isOn = value.includes(option.id);
        return (
          <button
            key={option.id}
            type="button"
            role="checkbox"
            aria-checked={isOn}
            onClick={() => toggle(option.id)}
            className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-sm text-foreground transition-colors hover:bg-accent"
          >
            <span
              className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                isOn ? 'border-brand-600 bg-brand-600 text-white' : 'border-border'
              }`}
            >
              {isOn && <Check size={12} />}
            </span>
            <Avatar name={option.name} size={22} />
            <span className="truncate">{option.name}</span>
          </button>
        );
      })}
    </div>
  );
}
